import { apiClient, v3Client } from '@api/apiClient';
import { serializeFacilityDetails } from '@common/facilityDetails';
import { V4ApiResponse } from '@api/types';
import { ToastType } from '@components/Toast/Toast';
import { ICustomerContact } from '@typeDefinitions/contactTypes';
import { FeatureFlagCustomer } from '@typeDefinitions/featureFlagTypes';
import { logError } from '@utils/errorLogging';
import { createError } from '@utils/errorUtils';
import { handleToast } from '@utils/utils';
import { CHANGE_TRACKING_SOURCES } from '@changeTracking/utils/changeTrackingRegistry';
import { buildChangeTrackingRequestConfig } from '@changeTracking/utils/buildChangeTrackingHeaders';
import {
  CustomerAddress,
  CustomerDetails,
  ediLog,
  ICreditCardPayment,
} from '@store/orders/types';

export async function fetchCustomerInfo(
  customerId: number,
): Promise<CustomerDetails> {
  try {
    const response: V4ApiResponse<CustomerDetails> = await apiClient.get(
      `/order/customerdetails/${customerId}`,
    );
    return response.value as CustomerDetails;
  } catch (error: unknown) {
    logError('fetchCustomerInfo', error as Error);
    throw createError(error, 'Failed to fetch customer info');
  }
}

export async function fetchEdiSettingsValueData(customerId: number) {
  try {
    const response: { value: Record<string, string[]> } = await apiClient.get(
      `EDIConfig/EDISettingValues/${customerId}?Id=${customerId}`,
    );
    return response.value;
  } catch (error: unknown) {
    logError('fetchEditSettingsCalueData', error as Error);
    handleToast(ToastType.Error, 'Failed to fetch EDI Settings Data Value');
    throw createError(error, 'Failed to fetch EDI Settings Data Value');
  }
}

export async function fetchFeatureFlagGetCustomerFeatures(
  customerId: number,
): Promise<FeatureFlagCustomer> {
  try {
    const response = await apiClient.get(
      `FeatureFlag/GetCustomerFeatures/${customerId}`,
    );
    return response as unknown as FeatureFlagCustomer;
  } catch (error: unknown) {
    logError('fetchFeatureFlagGetCustomerFeatures', error as Error);
    handleToast(ToastType.Error, 'Failed to fetch Feature Flag Get Customer');
    throw createError(error, 'Failed to fetch Feature Flag Get Customer');
  }
}

export async function setToAddressBook(
  address: CustomerAddress,
): Promise<boolean> {
  try {
    const response: { isSuccess: boolean } = await apiClient.post(
      '/CustomerAddress',
      address.facilityDetails
        ? {
            ...address,
            facilityDetails: serializeFacilityDetails(address.facilityDetails),
          }
        : address,
      buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.orderDetails.stopsAddToAddressBook,
      ]),
    );

    return response.isSuccess;
  } catch (error: unknown) {
    logError('setToAddressBook', error as Error);
    throw createError(error, 'Failed to add address to address book');
  }
}

export async function fetchEdiLogs(
  customerId: number,
  shipmentId: string | undefined,
): Promise<ediLog[]> {
  try {
    const response = await v3Client.get(
      `EDI/Transactions/{ShipmentId}/{CustomerId}?ShipmentId=${shipmentId}&CustomerId=${customerId}`,
    );
    return response as unknown as ediLog[];
  } catch (error: unknown) {
    logError('fetchEditLogs', error as Error);
    throw createError(error, 'Failed to fetch EDI transaction logs');
  }
}

export async function makeCreditCardPayments(
  orderId: number,
  paymentDetails: ICreditCardPayment,
) {
  try {
    await apiClient.post(
      `Email/PayWithCreditCard/${orderId}`,
      paymentDetails,
      buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.orderDetails
          .customerPaymentsSendCreditCardPayment,
      ]),
    );
    handleToast(
      ToastType.Success,
      'Customer Sent Credit Card Payments Information',
    );
  } catch (error: unknown) {
    logError('makeCreditCardPayments', error as Error);
    throw createError(error, 'Failed to send Credit Card Payments to Customer');
  }
}

export async function getCustomerContact(
  id: number,
): Promise<ICustomerContact[]> {
  try {
    const response: { isSuccess: boolean; value: ICustomerContact[] } =
      await apiClient.get(`/Customer/getcustomercontact/${id}`);
    return response.isSuccess ? response.value : [];
  } catch (error: unknown) {
    logError('getCustomerContact', error as Error);
    throw createError(error, 'Failed to get Customer Contact');
  }
}

export async function markPrePaymentProcessed(
  prePaymentId: string,
  orderId?: number,
) {
  try {
    const trackingConfig = buildChangeTrackingRequestConfig([
      CHANGE_TRACKING_SOURCES.orderDetails.customerInformation,
    ]);
    const res: V4ApiResponse<null> = await apiClient.put(
      '/Customer/customerprepayments',
      undefined,
      {
        ...trackingConfig,
        params: {
          ...(orderId != null && { order: orderId }),
          prepaymentId: prePaymentId,
        },
      },
    );
    if (Boolean(res.isSuccess)) {
      handleToast(
        ToastType.Success,
        'Pre-payment marked as processed successfully',
      );
      return true;
    }

    handleToast(ToastType.Error, 'Failed to mark pre-payment as processed');
    return false;
  } catch (error: unknown) {
    const e = createError(error, 'Failed to mark pre-payment as processed');
    handleToast(ToastType.Error, e.message);
  }
}
